import React, { useState } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { Box, Popover, usePopoverContext } from '@lyearn/ui';
import { FilterOperators } from '@/components/Filter/types';
import { AddMoreButton } from '@/components/FilterManager/components/ActionButton';
import { FilterBodyConfig, NewFilterOption } from '@/components/FilterNew/types';

import { NestedFilterProps } from '../types';
import { FilterBodyRenderer } from './FilterBodyRenderer';

export const NestedFilterManager = ({
  setFilter,
  filter,
  pillsConfig,
  setFilterIdToOpen,
  ...rest
}: NestedFilterProps) => {
  const { onClose } = usePopoverContext();
  const [selectedPath, setSelectedPath] = useState<
    {
      id: string;
      data: FilterBodyConfig<{}>;
    }[]
  >([
    {
      id: 'nested',
      data: {
        ...rest,
      },
    },
  ]);

  const {
    listConfig: { adaptSelectedFilterItemsToState },
  } = selectedPath[selectedPath.length - 1].data;

  const getAdaptedState = (items: NewFilterOption[], id: string) => {
    return adaptSelectedFilterItemsToState
      ? adaptSelectedFilterItemsToState(items, id, filter)
      : { [id]: items.map((i) => i.value) };
  };

  const setFiltersValue = (items: NewFilterOption[], id: string) => {
    const adaptedState = getAdaptedState(items, id);
    setFilter({
      ...filter,
      ...adaptedState,
    });
  };

  const onBack = () => {
    setSelectedPath((prev) => prev.slice(0, prev.length - 1));
  };

  const onChange = (id: string, items: NewFilterOption[], operators?: FilterOperators) => {
    const firstItem = items[0] as NewFilterOption | undefined;
    const configToRenderFilterBody = firstItem?.configToRenderFilterBody;
    const filterIdToOpen = firstItem?.value;

    // if configToRenderFilterBody.id is present in filter, then we need to call a function saying that this filter is already applied
    // a function will take the id and filter and return true and false;
    if (
      filterIdToOpen &&
      pillsConfig?.some((pill) => pill.isPillSelected(filter, filterIdToOpen))
    ) {
      // so let's say departmentId is already applied,
      // calling setFilterIdToOpen(departmentId) will open the department filter, instead of opening it from in the nested filter
      setFilterIdToOpen(filterIdToOpen);
      onClose();
      return;
    }

    // another function will accept the id and check
    if (configToRenderFilterBody) {
      setSelectedPath((prevState) => {
        return [
          ...prevState,
          {
            id,
            data: configToRenderFilterBody,
          },
        ];
      });
    } else {
      setFiltersValue(items, id);
      onClose();
    }
  };

  return (
    <React.Suspense fallback={null}>
      {selectedPath.map((path, index) => {
        // hide all but the last one, this is done to preserve state when going back
        return (
          <Box
            key={path.id}
            className={index === selectedPath.length - 1 ? 'filter-body' : 'hidden'}>
            <FilterBodyRenderer
              selectedPathConfig={path.data}
              onBack={selectedPath.length > 1 ? onBack : undefined}
              onChange={onChange}
            />
          </Box>
        );
      })}
    </React.Suspense>
  );
};

const BaseNestedFilter = (props: NestedFilterProps) => {
  const [isOpen, { toggle }] = useToggle(false);
  const { t } = useTranslation();
  const { isMobile } = useScreen();

  return (
    <Popover open={isOpen} onOpenChange={toggle}>
      {({ open }) => (
        <>
          <Popover.Button>
            <AddMoreButton>{t('Common:Filter.AddFilter')}</AddMoreButton>
          </Popover.Button>
          {open ? (
            <Popover.Content
              position="bottom-start"
              renderAs={isMobile ? 'bottom-sheet' : 'popover'}>
              <NestedFilterManager {...props} />
            </Popover.Content>
          ) : null}
        </>
      )}
    </Popover>
  );
};

export default BaseNestedFilter;
